import { useEffect, useReducer, Reducer, useCallback } from 'react'
import { request } from 'graphql-request'

import { gql } from '../gql'
import { Card, CardsQuery, UpdateCardMutation } from '../gql/graphql'

const CARDS_QUERY = gql(`
  query Cards {
    cards {
      id
      type
      workId
      status
      data {
        ... on NewLawsCardData {
          lawJournalId
          issueYear
          issueNumber
          issueDate
          newLaws {
            publishedLawId
            text
            evszam
            sorszam
            kibKod
            kibocsato
            tipus
            title
            lcode
            url
            status
            relevance
          }
        }
      }
      assignedTo {
        id
        name
      }
      createdAt
    }
  }
`)

const queryCards = async () => {
  const resp = await request<CardsQuery>('/api/graphql', CARDS_QUERY)
  return resp.cards as Card[]
}

const UPDATE_CARD = gql(`
  mutation updateCard($card: CardInput!) {
    updateCard(card: $card)
  }
`)

// TODO: ide jön a mutation variables
const mutateCard = async (changes: ChangeType) => {
  const variables = {
    card: changes,
  }

  const resp = await request<UpdateCardMutation>('/api/graphql', UPDATE_CARD, variables)
  return resp
}

export enum Modification {
  Untouched = 0,
  Modified = 1,
  Saved = 2,
}

export type NewLawsCardState = {
  id: number
  workId: string
  status: string
  lawJournalId: number
  issueYear: string
  issueNumber: number
  issueDate: string
  newLaws: NewLaw[]
  assignedTo: string
  createdAt: string
  isModified: Modification
}

export type NewLaw = {
  publishedLawId: number
  text: string
  evszam: string
  sorszam: string
  kibocsato: string
  kibKod: string
  tipus: string
  title: string
  lcode: string
  url: string

  status: string
  relevance: string
}

const mapCardsToState = (item: Card): NewLawsCardState => ({
  id: item.id,
  workId: item.workId,
  status: item.status,
  lawJournalId: item.data.lawJournalId,
  issueYear: item.data.issueYear,
  issueNumber: item.data.issueNumber,
  issueDate: item.data.issueDate,
  newLaws: item.data.newLaws.map((law) => ({
    ...law,
    status: law.status,
    relevance: law.relevance,
  })) as NewLaw[],
  assignedTo: item.assignedTo?.id,
  createdAt: item.createdAt,
  isModified: Modification.Untouched,
})

type PageState = {
  cards: NewLawsCardState[]
  modified: Array<number>
  isLoading: boolean
  isModified: Modification
}

type ActionType = {
  type: string
  id?: number
  changes?: ChangeType
  payload?: ChangeType[]
}

type ChangeType = {
  [name: string]: any
}

const reducer = (state: PageState, action: ActionType): PageState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true }
    case 'LOADED':
      return {
        cards: action.payload as NewLawsCardState[],
        isLoading: false,
        modified: [],
        isModified: Modification.Untouched,
      }
    case 'MODIFIED':
      const _index = state.cards.findIndex((card) => card.id === action.id)
      if (_index > -1) {
        const _card = Object.assign({}, state.cards[_index], action.changes, {
          isModified: Modification.Modified,
        })
        state.cards[_index] = _card
        const _modified = state.modified.includes(action.id)
          ? state.modified
          : state.modified.concat(action.id)
        return { ...state, modified: _modified, isModified: Modification.Modified }
      } else return state
    case 'SAVED':
      const _card = state.cards.find((card) => card.id === action.id)
      _card.isModified = Modification.Saved
      return { ...state }
    default:
      return state
  }
}

export const useCards = () => {
  const [state, dispatch] = useReducer<Reducer<PageState, ActionType>>(reducer, {
    cards: [],
    isLoading: false,
    modified: [],
    isModified: Modification.Untouched,
  })

  useEffect(() => {
    dispatch({ type: 'LOADING' })
    queryCards().then((list) => {
      dispatch({ type: 'LOADED', payload: list.map(mapCardsToState) })
    })
  }, [])

  const updateCard = useCallback(
    (id: number, changes: ChangeType) => {
      dispatch({ type: 'MODIFIED', id, changes })
      console.log('changes: ', changes, id)
    },
    [dispatch],
  )

  const saveCard = (id: number) => {
    const _card = state.cards.find((card) => card.id === id)

    mutateCard({
      id: _card.id,
      status: _card.status,
      assignedTo: _card.assignedTo,
      data: {
        lawJournalId: _card.lawJournalId,
        statuses: _card.newLaws.map((law) => ({
          id: law.publishedLawId,
          status: law.status,
        })),
      },
    }).then((resp) => {
      dispatch({ type: 'SAVED', id: id })
    })
  }

  return {
    cards: state.cards,
    isLoading: state.isLoading,
    isModified: state.isModified,
    updateCard,
    saveCard,
  }
}
